import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const CATEGORIES = ['all', 'gaming', 'education', 'community', 'custom'];
const CAT_COLORS = { gaming: '#ec4899', education: '#0ea5e9', community: '#10b981', custom: '#f59e0b' };

function TemplateCard({ tpl, onUse }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: 'linear-gradient(135deg,#1e1b4b,#312e81)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{tpl.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.name}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
            {tpl.category && tpl.category !== 'all' && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: `${CAT_COLORS[tpl.category] || '#6b7280'}22`, color: CAT_COLORS[tpl.category] || '#6b7280', border: `1px solid ${CAT_COLORS[tpl.category] || '#6b7280'}44` }}>{tpl.category}</span>
            )}
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{(tpl.usageCount || 0).toLocaleString()} uses</span>
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{tpl.description}</div>
      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
        <span>📁 {(tpl.channels || []).length} channels</span>
        <span>🎭 {(tpl.roles || []).length} roles</span>
      </div>
      <button onClick={() => onUse(tpl)}
        style={{ padding: '7px', borderRadius: 7, border: 'none', background: '#6366f1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        Use Template
      </button>
    </div>
  );
}

function PreviewPanel({ tpl, onConfirm, onBack, creating }) {
  const [serverName, setServerName] = useState(tpl.name);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>Review the template and give your server a name.</div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>Server Name</label>
        <input value={serverName} onChange={e => setServerName(e.target.value)}
          style={{ width: '100%', padding: '9px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Channels ({(tpl.channels || []).length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto' }}>
            {(tpl.channels || []).map((c, i) => (
              <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 10 }}>{c.type === 'voice' ? '🔊' : '#'}</span>
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Roles ({(tpl.roles || []).length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {(tpl.roles || []).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color || '#6b7280', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ color: r.color || 'rgba(255,255,255,0.55)' }}>{r.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
        <button onClick={onBack} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>← Back</button>
        <button onClick={() => onConfirm(serverName)} disabled={!serverName.trim() || creating}
          style={{ padding: '8px 20px', borderRadius: 7, background: serverName.trim() ? '#6366f1' : 'rgba(99,102,241,0.4)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: serverName.trim() ? 'pointer' : 'default' }}>
          {creating ? 'Creating…' : `Create "${serverName}"`}
        </button>
      </div>
    </div>
  );
}

export function ServerTemplatesModal() {
  const { isOpen, type, onClose, onOpen } = useModalStore();
  const addServer = useServerStore(s => s.addServer);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [query, setQuery]         = useState('');
  const [category, setCategory]   = useState('all');
  const [selected, setSelected]   = useState(null);
  const [creating, setCreating]   = useState(false);
  const [done, setDone]           = useState(null);

  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'serverTemplates');

  useEffect(() => {
    if (!isOpen || type !== 'serverTemplates') return;
    setSelected(null); setDone(null); setQuery(''); setCategory('all');
    setLoading(true);
    fetch('/api/templates', { headers: AUTH() })
      .then(r => r.json()).then(d => { setTemplates(d.templates || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [isOpen, type]);

  if (!isOpen || type !== 'serverTemplates') return null;

  const filtered = templates.filter(t => {
    const matchQ = !query || t.name.toLowerCase().includes(query.toLowerCase()) || t.description?.toLowerCase().includes(query.toLowerCase());
    const matchC = category === 'all' || t.category === category;
    return matchQ && matchC;
  });

  const handleConfirm = async (serverName) => {
    setCreating(true);
    try {
      const r = await fetch(`/api/templates/${selected.id}/apply`, { method: 'POST', headers: AUTH() });
      const d = await r.json();
      if (d.success) {
        const newServer = { id: `tpl-${Date.now()}`, name: serverName, icon: selected.icon };
        addServer?.(newServer);
        setDone(serverName);
        setSelected(null);
      }
    } catch {}
    setCreating(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label="Server Templates" onClick={e => e.stopPropagation()}
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, width: 700, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }}>

        <div style={{ padding: '20px 22px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>📋 Server Templates</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>Start with a pre-built server structure</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>

          {!selected && !done && (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>🔍</span>
                  <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search templates…"
                    style={{ width: '100%', padding: '9px 12px 9px 33px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.07)', flexWrap: 'wrap' }}>
                {CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setCategory(cat)}
                    style={{ padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: category === cat ? '#6366f1' : 'rgba(255,255,255,0.06)', border: `1px solid ${category === cat ? '#6366f1' : 'rgba(255,255,255,0.1)'}`, color: category === cat ? '#fff' : 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>
                    {cat}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 22px 22px' }}>
          {done ? (
            <div style={{ textAlign: 'center', paddingTop: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 48 }}>🎉</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>"{done}" is ready!</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Your server has been created from the template.</div>
              <button onClick={onClose} style={{ padding: '10px 28px', borderRadius: 8, background: '#6366f1', border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>Let's Go →</button>
            </div>
          ) : selected ? (
            <PreviewPanel tpl={selected} onConfirm={handleConfirm} onBack={() => setSelected(null)} creating={creating} />
          ) : loading ? (
            <div style={{ textAlign: 'center', paddingTop: 50, color: 'rgba(255,255,255,0.3)' }}>Loading templates…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', paddingTop: 50, color: 'rgba(255,255,255,0.3)' }}>No templates match.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {filtered.map(t => <TemplateCard key={t.id} tpl={t} onUse={setSelected} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
